import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Code,
  Divider,
  Drawer,
  Group,
  List,
  Modal,
  NumberInput,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconAlertTriangle, IconFlask, IconReceipt, IconSearch } from '@tabler/icons-react';
import {
  API_VERIFIED_CONFIRMATIONS,
  ORDER_CHECK_LABELS,
  ORDER_CONFIRMATION_LABELS,
  ORDER_EVENT_LABELS,
  ORDER_STATUS_LABELS,
  ORDER_PROVIDER_LABELS,
  ORDER_VERIFY_LABELS,
  SELLABLE_CONTENT_LABELS,
  SUBSCRIPTION_PLAN_LABELS,
  formatRubles,
  formatPlanDuration,
  type AdminOrderDetail,
  type AdminOrderRow,
  type AdminProductRow,
  type OrderStatus,
  type WouldGrant,
} from '@creativitas/shared';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/StateViews';
import { toUserMessage } from '@/lib/api';
import { ADMIN_USERS_KEY, fetchAdminUsers } from './api';
import {
  ADMIN_ORDERS_KEY,
  ADMIN_PAYMENTS_STATUS_KEY,
  ADMIN_PRODUCTS_KEY,
  createManualOrder,
  fetchOrder,
  fetchOrders,
  fetchPaymentsStatus,
  fetchProducts,
  grantOrder,
  linkOrder,
  noteOrder,
  reconcileOrders,
  refundOrder,
  verifyOrder,
} from './payments-api';

/**
 * Заказы (docs/PAYMENTS.md, §8, §10).
 *
 * Оплаченный, но некорректный заказ не закрывается молча: он ждёт в «На разбор»,
 * пока администратор не выдаст доступ или не отметит возврат — с причиной,
 * которая остаётся в журнале заказа.
 */

const STATUS_COLORS: Record<OrderStatus, string> = {
  received: 'gray',
  verifying: 'cyan',
  granted: 'teal',
  awaiting_account: 'blue',
  needs_review: 'orange',
  test: 'grape',
  refunded: 'red',
};

const TABS: { value: 'all' | OrderStatus; label: string }[] = [
  { value: 'needs_review', label: 'На разбор' },
  { value: 'verifying', label: 'Проверяются' },
  { value: 'awaiting_account', label: 'Ждут почты' },
  { value: 'granted', label: 'Выдано' },
  { value: 'test', label: 'Тестовые' },
  { value: 'refunded', label: 'Возвраты' },
  { value: 'all', label: 'Все' },
];

const dateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

export function AdminOrdersPage() {
  const [params, setParams] = useSearchParams();
  // Пока вкладку не выбрали: есть что разбирать — «На разбор», нет — все заказы.
  const [chosenTab, setTab] = useState<'all' | OrderStatus | null>(null);
  const [search, setSearch] = useState('');
  const [manualOpen, setManualOpen] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const openId = params.get('id');

  const orders = useQuery({ queryKey: ADMIN_ORDERS_KEY, queryFn: () => fetchOrders() });
  const status = useQuery({ queryKey: ADMIN_PAYMENTS_STATUS_KEY, queryFn: fetchPaymentsStatus });
  const live = status.data?.live ?? false;
  // В боевом режиме тестовые заказы не показываются: остаются в базе до очистки.
  const items = useMemo(
    () => (orders.data?.items ?? []).filter((order) => !(live && order.isTest)),
    [orders.data, live],
  );
  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const order of items) map.set(order.status, (map.get(order.status) ?? 0) + 1);
    return map;
  }, [items]);
  const tab = chosenTab ?? (counts.get('needs_review') ? 'needs_review' : 'all');
  const needle = search.trim().toLowerCase();
  const shown = (tab === 'all' ? items : items.filter((order) => order.status === tab)).filter(
    (order) =>
      !needle ||
      [order.externalOrderId, order.email, order.user?.email, order.productTitle, order.productCode].some((value) =>
        value?.toLowerCase().includes(needle),
      ),
  );
  const open = (id: string | null) => setParams(id ? { id } : {});

  return (
    <>
      <PageHeader
        eyebrow="Администрирование"
        title="Заказы"
        description="Оплаты через Robokassa. Заказ, который нельзя выдать автоматически, ждёт разбора: выдайте доступ или отметьте возврат — с причиной."
      />

      {status.data && !status.data.live ? (
        <Alert variant="light" color="grape" icon={<IconFlask size={16} />} mb="md">
          Тестовый режим: заказы проверяются и записываются, но доступ не выдают и писем покупателям не шлют.
          Боевой режим включается в «Тарифы и оплата».
        </Alert>
      ) : null}

      <Group mb="md" gap="sm" justify="space-between">
        <TextInput
          size="xs"
          w={300}
          leftSection={<IconSearch size={14} />}
          placeholder="Почта, № заказа или товар"
          aria-label="Поиск заказа"
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />
        <Group gap="sm">
          <Button size="xs" variant="light" onClick={() => setManualOpen(true)}>Добавить заказ вручную</Button>
          <Button size="xs" variant="subtle" onClick={() => setReconcileOpen(true)}>Сверка с Robokassa</Button>
        </Group>
      </Group>

      <Tabs value={tab} onChange={(value) => setTab((value as typeof tab) ?? 'needs_review')} mb="md">
        <Tabs.List>
          {TABS.filter((item) => !(live && item.value === 'test')).map((item) => (
            <Tabs.Tab key={item.value} value={item.value}>
              {item.label}
              {item.value !== 'all' && counts.get(item.value) ? ` · ${counts.get(item.value)}` : ''}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>

      {orders.isLoading ? (
        <LoadingState label="Загружаем заказы..." />
      ) : orders.isError ? (
        <ErrorState onRetry={() => void orders.refetch()} />
      ) : shown.length === 0 ? (
        <EmptyState
          icon={<IconReceipt size={28} />}
          title={needle ? 'Ничего не нашлось' : tab === 'needs_review' ? 'Разбирать нечего' : 'Заказов здесь нет'}
          description={needle && tab !== 'all' ? 'Поиск идёт по текущей вкладке — попробуйте «Все».' : undefined}
        />
      ) : (
        <Paper withBorder radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
          <ScrollArea>
            <Table miw={860} verticalSpacing="sm" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Оплачен</Table.Th>
                  <Table.Th>№ заказа</Table.Th>
                  <Table.Th>Почта</Table.Th>
                  <Table.Th>Товар</Table.Th>
                  <Table.Th>Сумма</Table.Th>
                  <Table.Th>Статус</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {shown.map((order) => (
                  <Table.Tr key={order.id} style={{ cursor: 'pointer' }} onClick={() => open(order.id)} data-testid={`order-${order.externalOrderId ?? order.id}`}>
                    <Table.Td><Text size="sm">{dateTime(order.paidAt)}</Text></Table.Td>
                    <Table.Td>
                      <Text size="sm" ff="monospace">{order.externalOrderId ?? '—'}</Text>
                      {order.provider === 'robokassa' ? (
                        <Text size="xs" c="var(--text-muted)">{ORDER_PROVIDER_LABELS.robokassa}</Text>
                      ) : null}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{order.email ?? '—'}</Text>
                      {order.user && order.user.email !== order.email ? (
                        <Text size="xs" c="var(--text-muted)">аккаунт: {order.user.email}</Text>
                      ) : null}
                    </Table.Td>
                    <Table.Td><Text size="sm">{order.productTitle ?? order.productCode ?? '—'}</Text></Table.Td>
                    <Table.Td><Text size="sm">{order.amountKopecks === null ? '—' : formatRubles(order.amountKopecks)}</Text></Table.Td>
                    <Table.Td><OrderStatusBadges order={order} /></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
      )}

      <Drawer opened={openId !== null} onClose={() => open(null)} title="Заказ" size="lg" position="right">
        {openId ? <OrderDetailView orderId={openId} robokassa={status.data?.robokassaVerification ?? false} /> : null}
      </Drawer>
      <ManualOrderModal
        opened={manualOpen}
        robokassa={Boolean(status.data?.robokassaVerification && status.data.live)}
        onClose={() => setManualOpen(false)}
        onCreated={(id) => {
          setManualOpen(false);
          open(id);
        }}
      />
      <ReconcileModal opened={reconcileOpen} onClose={() => setReconcileOpen(false)} onOpenOrder={(id) => { setReconcileOpen(false); open(id); }} />
    </>
  );
}

export function OrderStatusBadges({ order }: { order: Pick<AdminOrderRow, 'status' | 'checkCode' | 'isTest'> }) {
  return (
    <Group gap={4} wrap="wrap">
      <Badge size="sm" variant="light" color={STATUS_COLORS[order.status]}>{ORDER_STATUS_LABELS[order.status]}</Badge>
      {order.checkCode !== 'ok' ? (
        <Badge size="sm" variant="outline" color="orange">{ORDER_CHECK_LABELS[order.checkCode]}</Badge>
      ) : null}
    </Group>
  );
}

function wouldGrantText(would: WouldGrant | null, status: OrderStatus): string | null {
  if (!would) return null;
  const prefix = status === 'granted' ? 'Выдано' : 'Выдали бы';
  if (would.kind === 'game_ownership') return `${prefix}: владение игрой`;
  if (would.kind === 'content_access') return `${prefix}: бессрочный доступ к материалу (${SELLABLE_CONTENT_LABELS[would.contentKind].toLowerCase()})`;
  const plan = `${SUBSCRIPTION_PLAN_LABELS[would.plan]} +${formatPlanDuration(would.days, would.years)}`;
  if (would.effect === 'blocked') return `Лайт не выдаётся: у покупателя действует Про${would.endsAt ? ` до ${dateTime(would.endsAt)}` : ''}`;
  if (!would.userLinked) return `${prefix}: ${plan} — после подтверждения почты покупателем`;
  return `${prefix}: ${plan}, до ${dateTime(would.endsAt)}${would.effect === 'upgraded' ? `; Лайт прекращается, сгорает ${would.lostDays} дн.` : would.effect === 'extended' ? ' (продление)' : ''}`;
}

function OrderDetailView({ orderId, robokassa }: { orderId: string; robokassa: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: [...ADMIN_ORDERS_KEY, orderId], queryFn: () => fetchOrder(orderId), staleTime: 0 });
  const products = useQuery({ queryKey: ADMIN_PRODUCTS_KEY, queryFn: fetchProducts });
  const usersQuery = useQuery({ queryKey: ADMIN_USERS_KEY, queryFn: fetchAdminUsers });

  const onDone = (message: string) => (detail: AdminOrderDetail) => {
    queryClient.setQueryData([...ADMIN_ORDERS_KEY, orderId], detail);
    void queryClient.invalidateQueries({ queryKey: ADMIN_ORDERS_KEY });
    void queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY });
    notifications.show({ message, color: 'teal' });
  };
  const onError = (error: unknown) => notifications.show({ color: 'red', title: 'Не получилось', message: toUserMessage(error) });

  const verify = useMutation({ mutationFn: () => verifyOrder(orderId), onSuccess: onDone('Проверка в Robokassa выполнена'), onError });

  if (query.isLoading) return <LoadingState label="Загружаем заказ..." />;
  if (query.isError || !query.data) return <ErrorState onRetry={() => void query.refetch()} />;
  const order = query.data;
  const would = wouldGrantText(order.wouldGrant, order.status);
  const userOptions = (usersQuery.data?.items ?? []).map((row) => ({ value: row.user.id, label: `${row.user.displayName} — ${row.user.email}` }));
  const productOptions = (products.data?.items ?? []).map((product) => ({ value: product.id, label: `${product.title} (${product.code})` }));

  return (
    <Stack gap="md" data-testid="order-detail">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={4}>№ {order.externalOrderId ?? '—'}</Title>
          <Text size="xs" c="var(--text-muted)">
            {order.source === 'manual'
              ? 'добавлен вручную'
              : order.provider === 'robokassa'
                ? 'прямая оплата: уведомление Robokassa'
                : 'уведомление Tilda'}{' '}
            · доставок: {order.deliveryCount}
          </Text>
        </div>
        <OrderStatusBadges order={order} />
      </Group>

      {order.isTest ? (
        <Alert variant="light" color="grape" icon={<IconFlask size={16} />}>
          Тестовый заказ: доступ не выдаётся, писем покупателю нет. Ниже — что было бы с боевым.
        </Alert>
      ) : null}
      {order.status === 'verifying' ? (
        <Alert variant="light" color="cyan" icon={<IconAlertTriangle size={16} />}>
          Ждём, пока Robokassa подтвердит оплату: доступ выдаётся только после этого. Проверка повторяется раз в минуту;
          не подтвердится за 10 попыток — заказ уйдёт на разбор.
        </Alert>
      ) : null}
      {order.status === 'needs_review' && !order.isTest ? (
        <Alert variant="light" color="orange" icon={<IconAlertTriangle size={16} />} title="Заказ ждёт решения" data-testid="review-steps">
          <Stack gap="xs">
            <Text size="sm">
              {order.checkCode === 'payment_not_confirmed'
                ? 'Robokassa не подтвердила оплату'
                : `Выдать автоматически нельзя: ${ORDER_CHECK_LABELS[order.checkCode].toLowerCase()}`}
              {order.checkDetails ? ` (${order.checkDetails})` : ''}. Доступ не выдан, покупателю отправлено письмо, что заказ проверяется.
            </Text>
            <List type="ordered" size="sm" spacing={4}>
              <List.Item>
                {robokassa && order.externalOrderId ? (
                  <Group gap="xs">
                    <span>Проверьте оплату:</span>
                    <Button size="compact-xs" variant="light" loading={verify.isPending} onClick={() => verify.mutate()}>Проверить в Robokassa</Button>
                  </Group>
                ) : (
                  'Найдите операцию в кабинете Robokassa по номеру заказа.'
                )}
                {robokassa ? <Text size="xs" c="var(--text-muted)">Не подтвердилось — сверьте операцию в кабинете Robokassa.</Text> : null}
              </List.Item>
              <List.Item>Деньги пришли — «Выдать доступ» ниже, с короткой причиной.</List.Item>
              <List.Item>Денег нет — выдавать нечего: оставьте заметку. Покупатель просит вернуть — верните в кабинете Robokassa и «Отметьте возврат».</List.Item>
            </List>
          </Stack>
        </Alert>
      ) : null}

      <Table withRowBorders={false} verticalSpacing={4}>
        <Table.Tbody>
          <Row label="Почта в заказе">{order.email ?? '—'}</Row>
          <Row label="Аккаунт">{order.user ? `${order.user.displayName} — ${order.user.email}` : 'не привязан'}</Row>
          <Row label="Товар">{order.productTitle ?? order.productCode ?? '—'}</Row>
          <Row label="Сумма">{order.amountKopecks === null ? '—' : formatRubles(order.amountKopecks)}</Row>
          <Row label="Оплачен">{dateTime(order.paidAt)}</Row>
          <Row label="Проверка заказа">{ORDER_CHECK_LABELS[order.checkCode]}{order.checkDetails ? ` — ${order.checkDetails}` : ''}</Row>
          {would ? <Row label="Доступ">{would}</Row> : null}
          <Row label="Подтверждение оплаты">{ORDER_CONFIRMATION_LABELS[order.confirmation]}</Row>
          <Row label="Robokassa">{ORDER_VERIFY_LABELS[order.verifyStatus]}{order.verifyDetails ? ` — ${order.verifyDetails}` : ''}</Row>
          {order.purchaseRef ? <Row label="cid (справочно)">{order.purchaseRef}</Row> : null}
          {order.refundedAt ? <Row label="Возврат">{dateTime(order.refundedAt)}</Row> : null}
        </Table.Tbody>
      </Table>

      {order.items.length ? (
        <Paper withBorder p="xs" radius="md">
          <Text size="xs" fw={600} mb={4}>Позиции</Text>
          {order.items.map((item, index) => (
            <Text key={index} size="xs">
              {item.name ?? '—'} · код {item.code ?? 'нет'} · × {item.quantity ?? '?'} · {item.amountKopecks === null ? '—' : formatRubles(item.amountKopecks)}
            </Text>
          ))}
        </Paper>
      ) : null}

      {!order.isTest && robokassa && order.externalOrderId && order.status !== 'needs_review' ? (
        <Group>
          <Button size="xs" variant="light" loading={verify.isPending} onClick={() => verify.mutate()}>Проверить оплату в Robokassa</Button>
        </Group>
      ) : null}

      {!order.isTest && order.status === 'needs_review' ? (
        <GrantForm orderId={orderId} order={order} userOptions={userOptions} productOptions={productOptions} products={products.data?.items ?? []} onDone={onDone('Доступ выдан')} onError={onError} />
      ) : null}
      {!order.isTest && order.status === 'awaiting_account' ? (
        <LinkForm orderId={orderId} userOptions={userOptions} onDone={onDone('Заказ привязан')} onError={onError} />
      ) : null}
      {!order.isTest && ['granted', 'needs_review', 'awaiting_account'].includes(order.status) ? (
        <RefundForm orderId={orderId} onDone={onDone('Возврат отмечен')} onError={onError} />
      ) : null}
      <NoteForm orderId={orderId} onDone={onDone('Заметка добавлена')} onError={onError} />

      {order.emailPreview ? (
        <Paper withBorder p="sm" radius="md">
          <Text size="xs" fw={600}>{order.isTest ? 'Письмо, которое получил бы покупатель' : 'Письмо покупателю'}</Text>
          <Text size="xs" c="var(--text-muted)" mb={4}>{order.emailPreview.subject}</Text>
          <Code block style={{ whiteSpace: 'pre-wrap', fontSize: 11 }}>{order.emailPreview.text}</Code>
        </Paper>
      ) : null}

      <Divider label="Журнал заказа" labelPosition="left" />
      <Stack gap={6}>
        {order.events.map((event) => (
          <div key={event.id}>
            <Text size="xs" c="var(--text-muted)">{dateTime(event.at)}{event.actor ? ` · ${event.actor}` : ' · система'}</Text>
            <Text size="sm">{ORDER_EVENT_LABELS[event.action]}{event.details ? ` — ${event.details}` : ''}</Text>
          </div>
        ))}
      </Stack>
    </Stack>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Table.Tr>
      <Table.Td w={170}><Text size="xs" c="var(--text-muted)">{label}</Text></Table.Td>
      <Table.Td><Text size="sm">{children}</Text></Table.Td>
    </Table.Tr>
  );
}

type Done = (detail: AdminOrderDetail) => void;
type Options = { value: string; label: string }[];

function GrantForm({ orderId, order, userOptions, productOptions, products, onDone, onError }: {
  orderId: string; order: AdminOrderDetail; userOptions: Options; productOptions: Options; products: AdminProductRow[]; onDone: Done; onError: (error: unknown) => void;
}) {
  const defaultProduct = products.find((product) => product.code === order.productCode)?.id ?? null;
  const [productId, setProductId] = useState<string | null>(defaultProduct);
  const [userId, setUserId] = useState<string | null>(order.user?.id ?? null);
  const [note, setNote] = useState('');
  // Robokassa оплату не подтвердила — администратор подтверждает её сам, по кабинету Robokassa.
  // То же правило, что на сервере: уведомление Tilda или ResultURL прямой оплаты без подтверждения OpStateExt.
  const needsConfirmation = API_VERIFIED_CONFIRMATIONS.includes(order.confirmation) && order.verifyStatus !== 'paid';
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  useEffect(() => setProductId(defaultProduct), [defaultProduct]);
  const mutation = useMutation({
    mutationFn: () =>
      grantOrder(orderId, { productId: productId!, userId: userId!, note, ...(needsConfirmation ? { paymentConfirmed } : {}) }),
    onSuccess: onDone,
    onError,
  });

  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap="xs">
        <Text fw={600} size="sm">Выдать доступ по заказу</Text>
        <Select size="xs" label="Что выдать" data={productOptions} value={productId} onChange={setProductId} />
        <Select size="xs" label="Кому" searchable data={userOptions} value={userId} onChange={setUserId} placeholder="Найдите по имени или почте" />
        <Textarea size="xs" label="Причина решения" placeholder="Например: оплачено количество 2 — выдаём один Про, второй возвращаем" value={note} onChange={(event) => setNote(event.currentTarget.value)} minRows={2} />
        {needsConfirmation ? (
          <Checkbox
            size="xs"
            label={`Оплата подтверждена: операция № ${order.externalOrderId ?? '—'} успешна в кабинете Robokassa, сумма совпадает`}
            checked={paymentConfirmed}
            onChange={(event) => setPaymentConfirmed(event.currentTarget.checked)}
          />
        ) : null}
        <Group><Button size="xs" disabled={!productId || !userId || note.trim().length < 3 || (needsConfirmation && !paymentConfirmed)} loading={mutation.isPending} onClick={() => mutation.mutate()}>Выдать доступ</Button></Group>
      </Stack>
    </Paper>
  );
}

function LinkForm({ orderId, userOptions, onDone, onError }: { orderId: string; userOptions: Options; onDone: Done; onError: (error: unknown) => void }) {
  const [userId, setUserId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const mutation = useMutation({ mutationFn: () => linkOrder(orderId, { userId: userId!, note }), onSuccess: onDone, onError });
  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap="xs">
        <Text fw={600} size="sm">Привязать к пользователю</Text>
        <Text size="xs" c="var(--text-muted)">
          Заказ ждёт, пока покупатель подтвердит почту из заказа. Если в почте опечатка, а покупатель — существующий пользователь, привяжите заказ к нему: доступ выдастся сразу.
        </Text>
        <Select size="xs" label="Пользователь" searchable data={userOptions} value={userId} onChange={setUserId} />
        <Textarea size="xs" label="Почему" placeholder="Например: покупатель написал с почты аккаунта, в заказе опечатка" value={note} onChange={(event) => setNote(event.currentTarget.value)} minRows={2} />
        <Group><Button size="xs" disabled={!userId || note.trim().length < 3} loading={mutation.isPending} onClick={() => mutation.mutate()}>Привязать и выдать</Button></Group>
      </Stack>
    </Paper>
  );
}

function RefundForm({ orderId, onDone, onError }: { orderId: string; onDone: Done; onError: (error: unknown) => void }) {
  const [open, setOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [note, setNote] = useState('');
  const mutation = useMutation({ mutationFn: () => refundOrder(orderId, { note, refundConfirmed: true }), onSuccess: onDone, onError });
  if (!open) return <Group><Button size="xs" variant="subtle" color="red" onClick={() => setOpen(true)}>Отметить возврат…</Button></Group>;
  return (
    <Paper withBorder p="sm" radius="md" style={{ borderColor: 'var(--mantine-color-red-3)' }}>
      <Stack gap="xs">
        <Text fw={600} size="sm">Возврат</Text>
        <Text size="xs" c="var(--text-muted)">
          Сначала верните деньги в кабинете Robokassa. Здесь возврат только отмечается: доступ по заказу отзывается, срок пересчитывается. Прекращённый при переходе на Про Лайт сам не вернётся.
        </Text>
        <Checkbox size="xs" label="Деньги возвращены в кабинете Robokassa" checked={confirmed} onChange={(event) => setConfirmed(event.currentTarget.checked)} />
        <Textarea size="xs" label="Причина" value={note} onChange={(event) => setNote(event.currentTarget.value)} minRows={2} />
        <Group>
          <Button size="xs" color="red" disabled={!confirmed || note.trim().length < 3} loading={mutation.isPending} onClick={() => mutation.mutate()}>Отметить возврат</Button>
          <Button size="xs" variant="subtle" onClick={() => setOpen(false)}>Отмена</Button>
        </Group>
      </Stack>
    </Paper>
  );
}

function NoteForm({ orderId, onDone, onError }: { orderId: string; onDone: Done; onError: (error: unknown) => void }) {
  const [note, setNote] = useState('');
  const mutation = useMutation({ mutationFn: () => noteOrder(orderId, { note }), onSuccess: (detail) => { setNote(''); onDone(detail); }, onError });
  return (
    <Group gap="xs" align="flex-end" wrap="nowrap">
      <TextInput size="xs" style={{ flex: 1 }} label="Заметка в журнал" placeholder="Например: написали покупателю, ждём ответа" value={note} onChange={(event) => setNote(event.currentTarget.value)} />
      <Button size="xs" variant="light" disabled={note.trim().length < 3} loading={mutation.isPending} onClick={() => mutation.mutate()}>Добавить</Button>
    </Group>
  );
}

function ManualOrderModal({ opened, robokassa, onClose, onCreated }: { opened: boolean; robokassa: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const queryClient = useQueryClient();
  const products = useQuery({ queryKey: ADMIN_PRODUCTS_KEY, queryFn: fetchProducts, enabled: opened });
  const [invoiceId, setInvoiceId] = useState('');
  const [email, setEmail] = useState('');
  const [productId, setProductId] = useState<string | null>(null);
  const [amount, setAmount] = useState<number>(0);
  const [paidAt, setPaidAt] = useState('');
  const [checked, setChecked] = useState(false);
  const [comment, setComment] = useState('');
  const selected = products.data?.items.find((product) => product.id === productId);
  useEffect(() => { if (selected?.priceKopecks) setAmount(selected.priceKopecks / 100); }, [selected?.priceKopecks]);

  const mutation = useMutation({
    mutationFn: () =>
      createManualOrder({
        invoiceId: invoiceId.trim(),
        email: email.trim(),
        productId: productId!,
        amountRub: amount,
        paidAt: new Date(paidAt).toISOString(),
        attestation: checked ? { checkedInRobokassa: true, comment } : null,
      }),
    onSuccess: (detail) => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_ORDERS_KEY });
      notifications.show({ message: 'Заказ добавлен', color: 'teal' });
      onCreated(detail.id);
    },
    onError: (error) => notifications.show({ color: 'red', title: 'Заказ не добавлен', message: toUserMessage(error) }),
  });

  const attestationNeeded = !robokassa;
  const ready = /^\d+$/.test(invoiceId.trim()) && email.includes('@') && productId && amount > 0 && paidAt && (!attestationNeeded || (checked && comment.trim().length >= 10));

  return (
    <Modal opened={opened} onClose={onClose} title="Заказ, уведомление о котором не пришло" size="lg">
      <Stack gap="xs">
        <Text size="xs" c="var(--text-muted)">
          Только для оплаты, которая точно прошла: {robokassa ? 'Robokassa проверит её по API; если не ответит — подтвердите вручную.' : 'проверка в Robokassa по API не настроена — сверьте операцию в кабинете Robokassa и подтвердите ниже.'}
        </Text>
        <TextInput size="xs" label="Номер счёта Robokassa (InvId)" value={invoiceId} onChange={(event) => setInvoiceId(event.currentTarget.value)} />
        <TextInput size="xs" label="Почта покупателя" value={email} onChange={(event) => setEmail(event.currentTarget.value)} />
        <Select size="xs" label="Товар" data={(products.data?.items ?? []).map((product) => ({ value: product.id, label: `${product.title} (${product.code})` }))} value={productId} onChange={setProductId} />
        <NumberInput size="xs" label="Сумма, ₽" value={amount} min={0} onChange={(value) => setAmount(typeof value === 'number' ? value : Number(value) || 0)} />
        <TextInput size="xs" type="datetime-local" label="Когда оплачено" value={paidAt} onChange={(event) => setPaidAt(event.currentTarget.value)} />
        <Checkbox size="xs" label="Я сверил(а) эту операцию в кабинете Robokassa: номер, сумма, статус «успешно»" checked={checked} onChange={(event) => setChecked(event.currentTarget.checked)} />
        <Textarea size="xs" label="Что сверено" placeholder="Например: операция 1554967162 от 14.10, 800 ₽, статус «Оплачено»" value={comment} onChange={(event) => setComment(event.currentTarget.value)} minRows={2} />
        <Group justify="flex-end">
          <Button size="xs" variant="subtle" onClick={onClose}>Отмена</Button>
          <Button size="xs" disabled={!ready} loading={mutation.isPending} onClick={() => mutation.mutate()}>Добавить заказ</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function ReconcileModal({ opened, onClose, onOpenOrder }: { opened: boolean; onClose: () => void; onOpenOrder: (id: string) => void }) {
  const [text, setText] = useState('');
  const mutation = useMutation({ mutationFn: () => reconcileOrders(text) });
  const rows = mutation.data?.items ?? [];
  return (
    <Modal opened={opened} onClose={onClose} title="Сверка с Robokassa" size="lg">
      <Stack gap="xs">
        <Text size="xs" c="var(--text-muted)">
          Вставьте номера успешных операций из кабинета Robokassa — по одному в строке или через запятую. Номера, которых нет в журнале, — оплаты без уведомления: добавьте их вручную.
        </Text>
        <Textarea minRows={4} value={text} onChange={(event) => setText(event.currentTarget.value)} />
        <Group><Button size="xs" loading={mutation.isPending} onClick={() => mutation.mutate()}>Сверить</Button></Group>
        {rows.length ? (
          <Table verticalSpacing={4}>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.invoiceId}>
                  <Table.Td><Text size="sm" ff="monospace">{row.invoiceId}</Text></Table.Td>
                  <Table.Td>
                    {row.status ? (
                      <Group gap={4}>
                        <Badge size="sm" variant="light" color={STATUS_COLORS[row.status]}>{ORDER_STATUS_LABELS[row.status]}</Badge>
                        <Button size="compact-xs" variant="subtle" onClick={() => onOpenOrder(row.orderId!)}>Открыть</Button>
                      </Group>
                    ) : (
                      <Badge size="sm" color="red">нет в журнале</Badge>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : null}
      </Stack>
    </Modal>
  );
}
